import { useState, useEffect } from 'react';

interface UseIOSInstallPromptReturn {
  showPrompt: boolean;
  hidePrompt: () => void;
}

export const useIOSInstallPrompt = (): UseIOSInstallPromptReturn => {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    const checkShouldShowPrompt = () => {
      // Check if running on iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      // Check if running in Safari (not Chrome or other browsers on iOS)
      const isSafari =
        /Safari/.test(navigator.userAgent) &&
        !/Chrome|CriOS|OPiOS|FxiOS/.test(navigator.userAgent);

      // Check if already installed (running in standalone mode)
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;

      // Check user preferences
      const neverShow =
        localStorage.getItem('ios-install-prompt-never') === 'true';
      const lastDismissed = localStorage.getItem(
        'ios-install-prompt-dismissed'
      );

      // Don't show again for 7 days after dismissal
      const daysSinceDismissal = lastDismissed
        ? (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24)
        : Infinity;

      // Check if user has visited the site multiple times (more likely to install)
      const visitCount = parseInt(localStorage.getItem('visit-count') || '0');
      localStorage.setItem('visit-count', (visitCount + 1).toString());

      // Show prompt if:
      // - On iOS Safari
      // - Not already installed
      // - User hasn't opted out
      // - Either first time or 7+ days since last dismissal
      // - User has visited at least 2 times (shows engagement)
      const shouldShow =
        isIOS &&
        isSafari &&
        !isStandalone &&
        !neverShow &&
        daysSinceDismissal >= 7 &&
        visitCount >= 2;

      if (shouldShow) {
        // Small delay to let the app load first
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, 3000); // 3 second delay

        return () => clearTimeout(timer);
      }
    };

    // Only check on initial load
    checkShouldShowPrompt();
  }, []);

  const hidePrompt = () => {
    setShowPrompt(false);
  };

  return {
    showPrompt,
    hidePrompt,
  };
};
